import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams } from 'ag-grid-community';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ISalesOrder } from '../../../order/interfaces/order.interface';
import { PaginationConfig } from '../../../shared/configs/paginator.config';
import { IPagination } from '../../../shared/interfaces/paginator.interface';
import { AgGridPaginationUtilService } from '../../../shared/services/util/ag-grid-pagination-util.service';

interface SelectedState {
  all :boolean;
  some :boolean;
}
interface AgGridPaginationData {
  pagination :IPagination;
  selectedRows :ISalesOrder[];
  isSelectAllRows :boolean;
}

@Component({
  selector: 'lum-ui-select-all-ag-grid',
  templateUrl: './ui-select-all-ag-grid.component.html',
  styleUrls: ['./ui-select-all-ag-grid.component.scss'],
})
export class UiSelectAllAgGridComponent
  implements OnInit, OnDestroy, IHeaderAngularComp
{
  params :IHeaderParams;
  selectedState :SelectedState = {
    all: false,
    some: false,
  };

  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    per_page: PaginationConfig.perPage,
    to: null,
    total: 0,
  };

  selectedRows :ISalesOrder[] = [];
  isSelectAllRows :boolean = false;

  private onDestroy$ = new Subject<void>();

  constructor(
    private cd :ChangeDetectorRef,
    private agGridPaginationUtilService :AgGridPaginationUtilService,
  ) {}

  ngOnInit() :void {
    this.handlePaginationChanges(
      this.agGridPaginationUtilService.paginationChanged$,
      this.onPaginationChanged.bind(this),
    );

    this.handlePaginationChanges(
      this.agGridPaginationUtilService.selectAllChanged$,
      this.onSelectAllChanged.bind(this),
    );
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  agInit(params :IHeaderParams) :void {
    this.params = params;
    this.updateSelectionState();

    this.params.api.addEventListener('rowSelected', this.onRowSelected.bind(this));
  }

  onCheckboxChanged(event :any) :void {
    if (this.selectedState.all) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }

  refresh(params :IHeaderParams) :boolean {
    this.updateSelectionState();

    return false;
  }

  isRowSelectable(node :any) :boolean {
    return node.data?.isPosted;
  }

  isRowAlreadySelected(salesOrderId :number | string) :boolean {
    return this.selectedRows.some((row) => row.id === salesOrderId);
  }

  selectAll() :void {
    this.iterateCurrentPageRows((node) => {
      if (this.isRowSelectable(node)) {
        node.setSelected(true);
      }
    });
    this.updateSelectionState();
  }

  deselectAll() :void {
    this.iterateCurrentPageRows((node) => node.setSelected(false));
    this.updateSelectionState();
  }

  private updateSelectionState() :void {
    this.selectedState = {
      all: false,
      some: false,
    };

    this.selectedState.all = this.allRowsSelectedInCurrentPage();
    this.selectedState.some =
      this.someRowsSelectedInCurrentPage() && !this.selectedState.all;
    this.cd.detectChanges();
  }

  private allRowsSelectedInCurrentPage() :boolean {
    let allSelected = true;

    this.iterateCurrentPageRows((node) => {
      if (this.isRowSelectable(node) && !node.isSelected()) {
        allSelected = false;
      }
    });

    return allSelected;
  }

  private someRowsSelectedInCurrentPage() :boolean {
    let someSelected = false;

    this.iterateCurrentPageRows((node) => {
      if (this.isRowSelectable(node) && node.isSelected()) {
        someSelected = true;
      }
    });

    return someSelected;
  }

  private iterateCurrentPageRows(callback :(node :any) => void) :void {
    const numberIterate = this.pagination.per_page;
    let processedNodes = 0;
    let currentIndex = 0;

    while (processedNodes < numberIterate) {
      const node = this.params.api.getDisplayedRowAtIndex(currentIndex);

      if (!node) {
        break;
      }

      if (node.group && node.allLeafChildren) {
        for (const childNode of node.allLeafChildren) {
          if (processedNodes >= numberIterate) {
            break;
          }
          callback(childNode);
          processedNodes++;

          if (node.expanded) {
            currentIndex++;
          }
        }
      } else {
        callback(node);
        processedNodes++;
      }
      currentIndex++;
    }
  }

  onPaginationChanged() :void {
    this.iterateCurrentPageRows((node) => {
      if (this.isSelectAllRows) {
        if (node.data && this.isRowSelectable(node)) {
          node.setSelected(true);
        }
      } else if (
        node.data &&
        this.isRowSelectable(node) &&
        this.isRowAlreadySelected(node.data.id)
      ) {
        node.setSelected(true);
      }
    });

    this.updateSelectionState();
  }

  onSelectAllChanged() :void {
    this.iterateCurrentPageRows((node) => {
      if (this.isSelectAllRows) {
        if (node.data && this.isRowSelectable(node)) {
          node.setSelected(true);
        }
      } else {
        node.setSelected(false);
      }
    });

    this.updateSelectionState();
  }

  onRowSelected() :void {
    this.updateSelectionState();
  }

  private handlePaginationChanges(
    observable$ :Observable<AgGridPaginationData>,
    callback :() => void,
  ) :void {
    observable$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ pagination, selectedRows, isSelectAllRows }) => {
        this.pagination = pagination;
        this.selectedRows = selectedRows;
        this.isSelectAllRows = isSelectAllRows;
        setTimeout(callback, 50);
      });
  }
}
